import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon, LinkExternalIcon } from '@persona-id/design-tokens/icons';
import { capitalize } from 'lodash/fp';
import { FC, PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import Tooltip from 'dashboard/components/Tooltip';
import WorkflowModuleCallersList, {
  CallerUIState,
  Container,
} from 'dashboard/components/WorkflowModuleCallersList/WorkflowModuleCallersList';
import {
  WorkflowModuleAttributes,
  WorkflowModulePropagationResult,
  WorkflowModulePropagationStatus,
} from 'dashboard/models/workflow-module';
import PropagationStatusSticker from 'dashboard/views/DashboardWorkflowModulesShowView/PropagationStatusSticker';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';

const getPropagationResultMessage = (result: WorkflowModulePropagationResult) => {
  switch (result.status) {
    case WorkflowModulePropagationStatus.Completed:
      return `${result.meta['num-steps-updated']} module ${result.meta['num-steps-updated'] === 1 ? 'step' : 'steps'} updated.`;
    case WorkflowModulePropagationStatus.Failed:
    case WorkflowModulePropagationStatus.Skipped:
      return `${capitalize(result.meta.reason)}. Please update the workflow directly.`;
    case WorkflowModulePropagationStatus.NotFound:
      return 'Workflow does not exist.';
  }
};

const ReferencedWorkflowItem = styled.div`
  padding: ${Spacing[3]};
  display: grid;
  grid-template-columns: 35% 50% 15%;
  background: ${({ theme }) => theme.color.primary.white};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLink = styled('a')`
  display: flex;
  align-items: center;
`;

const IconContainer = styled.span`
  display: flex;
  margin-left: ${Spacing[1]};
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

interface PropagationModalContentProps {
  callers: WorkflowModuleAttributes['callers'];
  callerUIStates: CallerUIState[];
  latestPublishedBlockId?: string;
  propagationResults: WorkflowModulePropagationResult[];
  onUpdateCallerUIState: (config: CallerUIState, index: number) => void;
}

const PropagationModalContent: FC<PropsWithChildren<PropagationModalContentProps>> = ({
  callers,
  callerUIStates,
  latestPublishedBlockId,
  propagationResults,
  onUpdateCallerUIState,
}) => {
  const completedResults = useMemo(
    () =>
      propagationResults.filter(
        ({ status }) => status === WorkflowModulePropagationStatus.Completed,
      ),
    [propagationResults],
  );

  if (propagationResults.length) {
    return (
      <Container>
        <Accordion
          title={
            <Label
              weight='bold'
              color='primary'
            >{`Workflows successfully updated (${completedResults.length})`}</Label>
          }
          titleGapSize={Spacing[1]}
        >
          <Spacer height={1} />
          {propagationResults.map((result) => {
            const { status, workflow } = result;
            const workflowName =
              callers.find(({ 'source-id': workflowId }) => workflowId === workflow)?.[
                'source-name'
              ] || '';
            const message = getPropagationResultMessage(result);
            return (
              <ReferencedWorkflowItem
                key={workflow}
                aria-label={`Result line item for workflow ${workflowName}`}
              >
                <Label>{workflowName}</Label>
                <StyledLink
                  href={`/dashboard/workflows/${workflow}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {workflow}
                  <IconContainer>
                    <LinkExternalIcon />
                  </IconContainer>
                </StyledLink>
                <FlexContainer>
                  <PropagationStatusSticker status={status} />
                  {!!message && (
                    <Tooltip
                      trigger='hover'
                      tooltip={
                        <Paragraph color='primary' size='sm'>
                          {message}
                        </Paragraph>
                      }
                      hideArrow={true}
                      placement='top-start'
                    >
                      <IconContainer>
                        <CircleInfoIcon />
                      </IconContainer>
                    </Tooltip>
                  )}
                </FlexContainer>
              </ReferencedWorkflowItem>
            );
          })}
        </Accordion>
      </Container>
    );
  }

  if (!callers.length) {
    return (
      <Paragraph>
        This module has no usage. Update your workflows to leverage this module by using the Module
        step type.
      </Paragraph>
    );
  }

  return (
    <div>
      <Paragraph color='primary'>
        Select workflows below to update them to use the module&apos;s latest published version.
      </Paragraph>
      <Spacer height={2} />
      <WorkflowModuleCallersList
        callers={callers}
        callerUIStates={callerUIStates}
        latestPublishedBlockId={latestPublishedBlockId}
        selectable
        onUpdateCallerUIState={onUpdateCallerUIState}
      />
    </div>
  );
};

export default PropagationModalContent;
